/**
 * A button that acknowledges the press, and stays exactly where it is.
 *
 * Chosen deliberately over the magnetic button that follows the cursor. A
 * control that moves away from the pointer lies about where it is: it costs a
 * reader with a tremor or a trackpad an extra attempt, and it costs a reader
 * using switch access or a screen reader nothing at all except a target whose
 * on-screen position no longer matches its accessibility rectangle. The
 * feedback here is scale on press — inside the hit area, never displacing it.
 *
 * Renders `<a>` or `<button>` from the same component so the two never drift
 * apart visually while differing in what they actually are.
 */
import { m } from 'motion/react'
import type { ReactNode } from 'react'

import { useReducedMotionSafe } from './motion'

type Common = { children: ReactNode; className?: string; tone?: 'primary' | 'ghost' }

export function PressPlate(
  props: Common & ({ href: string; onClick?: never } | { href?: never; onClick: () => void }),
) {
  const { children, className, tone = 'primary' } = props
  const reduced = useReducedMotionSafe()
  const cls = `kl-plate kl-plate--${tone} ${className ?? ''}`

  const motionProps = reduced
    ? {}
    : { whileTap: { scale: 0.97 }, transition: { type: 'spring' as const, stiffness: 600, damping: 30 } }

  if (props.href !== undefined) {
    return (
      <m.a href={props.href} className={cls} {...motionProps}>
        {children}
      </m.a>
    )
  }

  return (
    <m.button type="button" onClick={props.onClick} className={cls} {...motionProps}>
      {children}
    </m.button>
  )
}
